import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { theme as designSystemTheme } from '@openx/design-system/theme/theme';
import type {} from '@openx/design-system/theme/tokens/typography';
import type { JSX } from 'react';
import type { PageHeaderProps } from './PageHeader';

type PageHeaderDesignSystemProps = Pick<PageHeaderProps, 'children' | 'title' | 'subtitle' | 'render' | 'sticky'>;

/**
 * Design-system variant of `PageHeader` (title row of the design-system `AppShell` template), picked by `PageHeader`
 * through its `designSystem` prop; render `PageHeader`, not this.
 *
 * Not implemented until UX specifies them: `goBack` / `customGoBackText` / `backButtonTestId`,
 * `emptyMode` / `emptyModeRender`, `titleBadge`, `PreTitleIconComponent`; pages using them stay on the current variant.
 */
export const PageHeaderDesignSystem = ({
  children,
  title,
  subtitle,
  render,
  sticky = false,
}: PageHeaderDesignSystemProps): JSX.Element => (
  // Fills the page like the current content container (`flexGrow: 1`), otherwise the app background shows below short
  // content. The colour is read from the DS theme object: this Box is outside the ThemeProvider below, so the
  // `background.default` token would resolve in the caller's (current) theme.
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      backgroundColor: designSystemTheme.palette.background.default,
    }}
  >
    {/* Only the header row gets the DS theme: the page content below may still render components of the current
        theme. */}
    <ThemeProvider theme={designSystemTheme}>
      <Box
        component="header"
        data-test="page-header"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          px: 3,
          pt: 2,
          pb: 2,
          backgroundColor: 'background.default',
          // same z-index as the current sticky header, so the layering against page content stays the same
          ...(sticky && { position: 'sticky', top: 0, zIndex: 1001 }),
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {title ? (
            <Typography variant="h1" component="h1" data-test="page-header-title">
              {title}
            </Typography>
          ) : null}
          {/* `textSecondary`, not `text.secondary`: MUI 9 Typography ignores the dotted form and the text inherits the
              primary colour. `body1` follows the Deal Insights design (the DS AppShell template uses `body2`). */}
          {subtitle ? (
            <Typography variant="body1" color="textSecondary" sx={{ mt: 0.5 }} data-test="page-header-subtitle">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>{render?.()}</Box>
      </Box>
    </ThemeProvider>
    {children}
  </Box>
);
